import React, { useEffect, useMemo } from 'react'

type ConditionalFormFieldsProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: {
    getFieldValue: (field: any) => any
    deleteField: (field: any) => void
    setFieldValue: (field: any, updater: any) => void
  }
  fields: readonly string[]
  show: boolean
  children: React.ReactNode
}

/**
 * If TanStack Form fields are conditionally rendered using standard syntax,
 * their validation state is not cleared from the form store when they are unmounted.
 * This can cause stale validation errors from hidden fields to block submission silently.
 *
 * This component wraps conditionally-rendered form fields to prevent stale validation from
 * blocking submission. It replaces the standard `{ isVisible && <form.AppField /> }` syntax
 * with a declarative wrapper. When `show` becomes false, the wrapped fields are unmounted
 * and their validation state is cleared from the form store while preserving their values
 * for when they remount.
 *
 * Memoization note: `fields` is memoized internally via JSON.stringify so the
 * component is safe even if the caller passes a fresh array literal. However,
 * callers should still prefer defining `fields` arrays as module-level
 * constants (e.g. `const CHECK_NUMBER_FIELDS = ['checkNumber']`) to avoid
 * unnecessary work entirely.
 *
 * @example
 * ```tsx
 * const PAYMENT_FIELDS = ['checkNumber', 'checkDate']
 *
 * <ConditionalFormFields form={form} fields={PAYMENT_FIELDS} show={isCheckPayment}>
 *   <form.AppField name="checkNumber">
 *     {(field) => <TextField field={field} label="Check number" />}
 *   </form.AppField>
 *   <form.AppField name="checkDate">
 *     {(field) => <DateField field={field} label="Check date" />}
 *   </form.AppField>
 * </ConditionalFormFields>
 * ```
 */
export const ConditionalFormFields = ({
  form,
  fields,
  show,
  children,
}: ConditionalFormFieldsProps) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedFields = useMemo(() => fields, [JSON.stringify(fields)])
  useEffect(() => {
    if (!show) {
      for (const fieldName of memoizedFields) {
        // Get the current value of the field to restore it when the field is remounted
        const currentValue = form.getFieldValue(fieldName)
        // Delete the field from the form store to clear any stale validation errors
        form.deleteField(fieldName)
        // Restore the current value of the field if it is not undefined, null, or an empty string
        if (currentValue != null && currentValue !== '') {
          form.setFieldValue(fieldName, currentValue)
        }
      }
    }
  }, [show, form, memoizedFields])

  return show ? <>{children}</> : null
}
